import {
  AMBestCompany,
  AMBestParent, CarrierCreateRequest,
  CarrierEditRequest,
  CarrierIssuer,
  IssuingCompanyEditRequest,
} from '@/types/CarrierIssuer';
import { Service, QueryParams } from './api';

export interface LambdaFunction {
  name: string;
  logGroup: string;
  description: string;
}

export interface LambdaFunctionsResponse {
  functions: LambdaFunction[];
  count: number;
}

export interface LambdaLog {
  timestamp: string;
  message: string;
  logLevel: string;
}

export interface LambdaLogsResponse {
  logs: LambdaLog[];
  count: number;
}

export interface LambdaLogsParams {
  function: string;
  startTime?: string;
  endTime?: string;
  logLevel?: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
  correlationId?: string;
  limit?: number;
}

export interface BeanstalkEnvironment {
  name: string;
  logGroupName: string;
  status: string;
  health: string;
}

export interface BeanstalkEnvironmentsResponse {
  environments: BeanstalkEnvironment[];
  count: number;
}

export interface BeanstalkLog {
  timestamp: string;
  message: string;
  logLevel: string;
  logGroupName: string;
  logStreamName: string;
  correlationId?: string;
  userId?: number;
}

export interface BeanstalkLogsResponse {
  logs: BeanstalkLog[];
  count: number;
}

export interface BeanstalkLogsParams {
  ebEnvironment: string;
  logType?: string;
  startTime?: string;
  endTime?: string;
  logLevel?: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
  correlationId?: string;
  userId?: number;
  limit?: number;
}

export interface DlpAuditLog {
  userId: number | null;
  createdAt: string;
  requestUrl: string;
  metadata: string | null;
  queryParams: string | null;
}

export interface DlpAuditParams {
  startDate?: string;
  endDate?: string;
  userId?: number;
  categoryID?: number;
}

class AdminService extends Service {

  async updateCarrier(insurerGroupId: number, req: CarrierEditRequest) {
    return await this.put(`/v1/admin/carrier/${insurerGroupId}`, req).then(this.handleResponse);
  }

  async updateIssuingCompany(issuingCompanyId: number, req: IssuingCompanyEditRequest) {
    return await this.put(`/v1/admin/issuing-company/${issuingCompanyId}`, req).then(this.handleResponse);
  }

  async LoadCarrierIssuerList(): Promise<CarrierIssuer[]> {
    return await this.get<CarrierIssuer[]>('/v1/admin/carrier-issuer').then(this.handleResponse);
  }

  async CreateCarrier(req: CarrierCreateRequest): Promise<string> {
    return await this.post<string>('/v1/admin/carrier-issuer', req).then(this.handleInsertResponse);
  }

  async fetchAMBestParents(): Promise<Array<AMBestParent>> {
    return await this.get<AMBestParent[]>('/v1/admin/carrier/ambest/parents').then(this.handleResponse);
  }

  async fetchAMBestCompanies(): Promise<Array<AMBestCompany>> {
    return await this.get<AMBestCompany[]>('/v1/admin/carrier/ambest/companies').then(this.handleResponse);
  }

  async getLambdaFunctions(): Promise<LambdaFunctionsResponse> {
    return await this.get<LambdaFunctionsResponse>('/v1/admin/logs/lambda/group/functions').then(this.handleResponse);
  }

  async getLambdaLogs(params: LambdaLogsParams): Promise<LambdaLogsResponse> {
    return await this.get<LambdaLogsResponse>('/v1/admin/logs/lambda', params as unknown as QueryParams).then(this.handleResponse);
  }

  async getBeanstalkEnvironments(): Promise<BeanstalkEnvironmentsResponse> {
    return await this.get<BeanstalkEnvironmentsResponse>('/v1/admin/logs/beanstalk/group/environments').then(this.handleResponse);
  }

  async getBeanstalkLogs(params: BeanstalkLogsParams): Promise<BeanstalkLogsResponse> {
    return await this.get<BeanstalkLogsResponse>('/v1/admin/logs/beanstalk', params as unknown as QueryParams).then(this.handleResponse);
  }

  async getDlpAuditLogs(params?: DlpAuditParams): Promise<DlpAuditLog[]> {
    return await this.get<DlpAuditLog[]>('/v1/admin/logs/dlp-audit', params as unknown as QueryParams).then(this.handleResponse);
  }

  async seedDevUserServicingRoleMappings(): Promise<{ count: number }> {
    return await this.post<{ count: number }>('/v1/admin/seed/user-servicing-role-mappings', {}).then(this.handleResponse);
  }
}

export const adminService = new AdminService();